'use client'

import { useState } from 'react'
import { useTranslations } from 'next-intl'
import { useRouter } from '@bprogress/next/app'
import { useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeftIcon,
  TrashIcon,
  FileTextIcon,
  HashIcon,
  WaypointsIcon,
  CalculatorIcon,
  MessageCircleMoreIcon,
  FunnelIcon,
  GlobeIcon,
  BadgeDollarSignIcon,
  PercentIcon,
  TagIcon,
  RefreshCwIcon,
  CopyIcon
} from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { CodeBlock } from '@/components/ui/code-block'
import {
  ConfirmationDialog,
  ContentDetailInfo,
  ContentDetailFile,
  ContentError,
  DeleteConfirmationDialog,
  JobStatusBadge
} from '@/components/partials'
import { useProductPdfExport, useDeleteProductPdfExport, useCreateProductPdfExport, useCopyToClipboard } from '@/hooks'
import { formatNumber, tOptional } from '@/lib/utils'

type PropsType = {
  id: string
}

const ProductPdfExportShowSkeleton = () => (
  <div className="space-y-6">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-4">
        <Skeleton className="h-9 w-16" />
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64" />
        </div>
      </div>
      <Skeleton className="h-9 w-20" />
    </div>
    <Card>
      <CardHeader>
        <Skeleton className="h-6 w-48" />
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-6 w-32" />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  </div>
)

const Detail = ({ id }: PropsType) => {
  const t = useTranslations()
  const router = useRouter()
  const queryClient = useQueryClient()
  const { copyToClipboard } = useCopyToClipboard()
  const [deleteProductPdfExportId, setProductPdfExportId] = useState<string | null>(null)

  const { data: productPdfExport, isLoading, error } = useProductPdfExport(id)
  const { mutate: deleteProductPdfExport, isPending: isDeleting } = useDeleteProductPdfExport()
  const { mutate: createProductPdfExportMutate, isPending: isRegenerating } = useCreateProductPdfExport()

  const [isRegenerateConfirmOpen, setIsRegenerateConfirmOpen] = useState(false)

  const handleDelete = () => setProductPdfExportId(id)

  const handleRegenerate = () => {
    if (productPdfExport) {
      const regenerateParams = {
        ...(productPdfExport.filters ?? {}),
        locale: productPdfExport.locale,
        showPrices: productPdfExport.showPrices,
        showCover: productPdfExport.showCover,
        currencyCode: productPdfExport.currencyCode,
        discountRate: productPdfExport.discountRate
      }
      createProductPdfExportMutate(regenerateParams, {
        onSuccess: async (newExport: any) => {
          await queryClient.invalidateQueries({ queryKey: ['productPdfExports'] })
          toast.success(t('created_successfully'))
          router.push(`/products/export/pdf/${newExport.id}`)
        },
        onError: (error: any) => {
          console.error('Error regenerating productPdfExport:', error)
          toast.error(t('error_creating'))
        }
      })
    }
  }

  const confirmDeleteProductPdfExport = () => {
    if (deleteProductPdfExportId) {
      deleteProductPdfExport(deleteProductPdfExportId, {
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: ['productPdfExports'] })
          toast.success(t('deleted_successfully'))
          router.push('/products/export/pdf')
          setProductPdfExportId(null)
        },
        onError: error => {
          console.error('Error deleting productPdfExport:', error)
          toast.error(t('error_deleting'))
          setProductPdfExportId(null)
        }
      })
    }
  }

  if (isLoading || isDeleting) {
    return <ProductPdfExportShowSkeleton />
  }

  if (error || !productPdfExport) {
    return <ContentError error={error} listUrl="products/export/pdf" />
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 items-center justify-between gap-2 md:grid-cols-2">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => router.push('/products/export/pdf')}>
            <ArrowLeftIcon className="h-4 w-4" />
            {t('list')}
          </Button>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{t('pdf_export_details')}</h1>
          </div>
        </div>
        <div className="flex flex-row items-center justify-end gap-2">
          <Button variant="outline" onClick={() => setIsRegenerateConfirmOpen(true)} disabled={isRegenerating}>
            <RefreshCwIcon className="h-4 w-4" />
            <span className="hidden items-center gap-2 lg:flex">{t('regenerate')}</span>
          </Button>
          <Button variant="destructive" onClick={handleDelete}>
            <TrashIcon className="h-4 w-4" />
            <span className="hidden items-center gap-2 lg:flex">{t('delete')}</span>
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileTextIcon className="h-5 w-5" />
            {t('information')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-4">
            <div className="col-span-4 space-y-2 xl:col-span-1">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                <HashIcon className="h-4 w-4" />
                ID
              </div>
              <div className="flex items-center gap-1">
                <Badge variant="outline" className="font-mono text-xs">
                  {productPdfExport.id}
                </Badge>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(productPdfExport.id)}
                      className="h-6 w-6 p-0">
                      <CopyIcon className="h-3 w-3" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t('copy')}</TooltipContent>
                </Tooltip>
              </div>
            </div>
            <div className="col-span-4 space-y-2 xl:col-span-1">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                <GlobeIcon className="h-4 w-4" />
                {t('language')}
              </div>
              <Badge variant="outline" className="font-mono text-xs">
                {productPdfExport.locale}
              </Badge>
            </div>
            <div className="col-span-4 space-y-2 xl:col-span-1">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                <CalculatorIcon className="h-4 w-4" />
                {t('total')}
              </div>
              <Badge variant="outline" className="font-mono text-xs">
                {formatNumber(productPdfExport.total)}
              </Badge>
            </div>
            <div className="col-span-4 space-y-2 xl:col-span-1">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                <WaypointsIcon className="h-4 w-4" />
                {t('status')}
              </div>
              <JobStatusBadge status={productPdfExport.status} />
            </div>
            <div className="col-span-4 space-y-2 xl:col-span-1">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                <TagIcon className="h-4 w-4" />
                {t('show_prices')}
              </div>
              <Badge variant="outline" className="font-mono text-xs">
                {productPdfExport.showPrices ? t('yes') : t('no')}
              </Badge>
            </div>
            <div className="col-span-4 space-y-2 xl:col-span-1">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                <FileTextIcon className="h-4 w-4" />
                {t('show_cover')}
              </div>
              <Badge variant="outline" className="font-mono text-xs">
                {productPdfExport.showCover ? t('yes') : t('no')}
              </Badge>
            </div>
            <div className="col-span-4 space-y-2 xl:col-span-1">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                <BadgeDollarSignIcon className="h-4 w-4" />
                {t('currency')}
              </div>
              <Badge variant="outline" className="font-mono text-xs">
                {productPdfExport.currencyCode ?? '-'}
              </Badge>
            </div>
            <div className="col-span-4 space-y-2 xl:col-span-1">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                <PercentIcon className="h-4 w-4" />
                {t('discount_rate')}
              </div>
              <Badge variant="outline" className="font-mono text-xs">
                {productPdfExport.discountRate !== undefined && productPdfExport.discountRate !== null
                  ? `${productPdfExport.discountRate}%`
                  : '-'}
              </Badge>
            </div>
            <div className="col-span-4 space-y-2 xl:col-span-1">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                <MessageCircleMoreIcon className="h-4 w-4" />
                {t('message')}
              </div>
              <Badge variant="outline" className="block font-mono text-xs break-all whitespace-normal">
                {tOptional(t, productPdfExport.message ?? '-')}
              </Badge>
            </div>
            <div className="col-span-4 space-y-2">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                <FunnelIcon className="h-4 w-4" />
                {t('filters')}
              </div>
              {productPdfExport.filters ? (
                <CodeBlock code={JSON.stringify(productPdfExport.filters, null, 2)} language="json" />
              ) : (
                <Badge variant="outline" className="font-mono text-xs">
                  -
                </Badge>
              )}
            </div>
          </div>
          <Separator />
          <ContentDetailInfo entity={productPdfExport} />
        </CardContent>
      </Card>

      {productPdfExport.file && <ContentDetailFile file={productPdfExport.file} preview />}

      <ConfirmationDialog
        open={isRegenerateConfirmOpen}
        onCancel={() => setIsRegenerateConfirmOpen(false)}
        onConfirm={() => {
          setIsRegenerateConfirmOpen(false)
          handleRegenerate()
        }}
        description={t('regenerate_export_confirmation')}
        confirmText={t('regenerate')}
        isLoading={isRegenerating}
      />

      <DeleteConfirmationDialog
        open={deleteProductPdfExportId !== null}
        onCancel={() => setProductPdfExportId(null)}
        onConfirm={confirmDeleteProductPdfExport}
        isDeleting={isDeleting}
      />
    </div>
  )
}

export default Detail
